import { useEffect } from 'react';
import type { ReactNode } from 'react';

import { cn } from '@muzly/lib/utils';

/**
 * 화면 가운데 뜨는 모달의 공통 껍데기(시안의 다이얼로그 — 311px · 24px 라운드 흰 판,
 * 배경은 Figma `mobile_scream` `1909:7159` 의 `rgba(34,35,37,0.24)`).
 * 이어하기·의견 남기기 안내·이탈 방지 팝업이 같은 껍데기를 쓴다.
 *
 * 닫는 길은 둘을 여기서 든다: 배경 탭과 Esc. Esc 가 있는 이유는 하드웨어 키보드를 붙인
 * 아이패드/시뮬레이터에서 배경 탭이 유일한 출구면 모달이 갇힌 것처럼 보이기 때문이다.
 * 배경은 **보이지 않는 출구**라 눈에 보이는 닫기(X·버튼)는 내용 쪽이 따로 둔다.
 */
export const ModalShell = ({
  labelledBy,
  dismissLabel,
  onDismiss,
  className,
  children,
}: {
  /** 다이얼로그의 이름이 되는 제목 요소의 id. */
  labelledBy: string;
  /** 배경 버튼의 접근성 이름. */
  dismissLabel: string;
  onDismiss: () => void;
  className?: string;
  children: ReactNode;
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6">
      <button
        type="button"
        data-testid="modal-backdrop"
        aria-label={dismissLabel}
        onClick={onDismiss}
        className="absolute inset-0 bg-[rgba(34,35,37,0.24)]"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn(
          'relative w-full max-w-[311px] rounded-3xl bg-popover p-6 text-center shadow-xl',
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
};
